import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import { useI18n } from "../../i18n/I18nContext";
import { api, formatApiError, formatPrice } from "../../lib/api";

export default function AdminProperties() {
    const { t } = useI18n();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/properties")
            .then((r) => setList(r.data))
            .catch(() => setList([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const remove = async (id) => {
        if (!window.confirm(t("admin.confirm_delete"))) return;
        try {
            await api.delete(`/admin/properties/${id}`);
            toast.success(t("admin.deleted"));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10">
                <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">
                            {t("admin.dashboard")}
                        </div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("admin.properties")}</h1>
                    </div>
                    <Link
                        to="/admin/properties/new"
                        data-testid="admin-add-property"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-emerald hover:bg-brand-emerald-soft text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Plus className="w-4 h-4" /> {t("admin.new_property")}
                    </Link>
                </div>

                <div className="bg-white border border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-properties-table">
                        <thead className="bg-brand-paper text-[0.65rem] tracking-[0.18em] uppercase text-brand-navy">
                            <tr>
                                <th className="text-left px-4 py-3">Image</th>
                                <th className="text-left px-4 py-3">Titre</th>
                                <th className="text-left px-4 py-3">Type</th>
                                <th className="text-left px-4 py-3">Transaction</th>
                                <th className="text-left px-4 py-3">Prix</th>
                                <th className="text-left px-4 py-3">{t("admin.featured")}</th>
                                <th className="text-left px-4 py-3">{t("admin.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="text-center py-12 text-gray-500">Chargement…</td></tr>
                            ) : list.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-12 text-gray-500">Aucun bien.</td></tr>
                            ) : list.map((p) => (
                                <tr key={p.id} className="border-t border-gray-100" data-testid={`admin-row-${p.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="w-16 h-12 bg-gray-100 overflow-hidden">
                                            {p.images?.[0] ? (
                                                <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                            ) : null}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 max-w-xs">
                                        <div className="font-semibold text-brand-navy truncate">{p.title_fr}</div>
                                        <div className="text-xs text-gray-500">{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ""}</div>
                                    </td>
                                    <td className="px-4 py-3 capitalize">{p.type}</td>
                                    <td className="px-4 py-3">{p.transaction === "rent" ? t("featured.for_rent") : t("featured.for_sale")}</td>
                                    <td className="px-4 py-3 text-brand-emerald font-semibold whitespace-nowrap">{formatPrice(p.price, p.currency)}</td>
                                    <td className="px-4 py-3">
                                        {p.featured ? <Star className="w-4 h-4 text-brand-gold fill-current" /> : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Link
                                                to={`/admin/properties/${p.id}/edit`}
                                                data-testid={`admin-edit-${p.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-navy hover:bg-brand-navy-soft text-white text-xs uppercase tracking-wide transition-colors"
                                            >
                                                <Pencil className="w-3.5 h-3.5" /> {t("admin.edit")}
                                            </Link>
                                            <button
                                                onClick={() => remove(p.id)}
                                                data-testid={`admin-delete-${p.id}`}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs uppercase tracking-wide transition-colors"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" /> {t("admin.delete")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}
